import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoginModal from '../components/LoginModal';
import * as api from '../services/api';
import { convertGoogleDriveUrlForDisplay, getPlaceholderImage } from '../utils/imageUtils';
import './OrderDetails.css';

export default function OrderDetails() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { isAuthenticated } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    loadOrder();
  }, [orderId, isAuthenticated]);

  // Load order when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && showLoginModal) {
      setShowLoginModal(false);
      loadOrder();
    }
  }, [isAuthenticated, showLoginModal]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const orderData = await api.getOrder(orderId);
      setOrder(orderData);
    } catch (error) {
      console.error('Error loading order:', error);
      setError(error.response?.data?.message || 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'pending': '#ff9800',
      'confirmed': '#2196f3',
      'packed': '#9c27b0',
      'shipped': '#3f51b5',
      'delivered': '#4caf50',
      'cancelled': '#f44336',
      'returned': '#ff5722'
    };
    return statusColors[status?.toLowerCase()] || '#666';
  };

  const getStatusIcon = (status) => {
    const statusIcons = {
      'pending': '⏳',
      'confirmed': '✅',
      'packed': '📦',
      'shipped': '🚚',
      'delivered': '🎉',
      'cancelled': '❌',
      'returned': '↩️'
    };
    return statusIcons[status?.toLowerCase()] || '📋';
  };

  if (loading) {
    return (
      <div className="order-details-container">
        <div className="loading-state">
          <p>Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-details-container">
        <div className="error-state">
          <h2>Order Not Found</h2>
          <p>{error || 'The order you are looking for does not exist.'}</p>
          <button onClick={() => navigate('/orders')} className="btn primary">
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="order-details-container">
      <div className="order-details-header">
        <button onClick={() => navigate('/orders')} className="back-button">
          ← Back to Orders
        </button>
        <h1>Order Details</h1>
      </div>

      <div className="order-details-content">
        {/* Order Info Card */}
        <div className="order-info-card">
          <div className="order-header">
            <div className="order-id-section">
              <h2>Order #{order._id.slice(-8).toUpperCase()}</h2>
              <div className="order-date">Placed on {formatDate(order.createdAt)}</div>
            </div>
            <div 
              className="order-status-badge"
              style={{ backgroundColor: getStatusColor(order.status) + '20', color: getStatusColor(order.status) }}
            >
              <span className="status-icon">{getStatusIcon(order.status)}</span>
              <span className="status-text">{order.status || 'Pending'}</span>
            </div>
          </div>
        </div>

        {/* Order Items */}
        <div className="order-section">
          <h3>Order Items</h3>
          <div className="order-items-list">
            {order.items?.map((item, index) => {
              // Debug: Log the entire item structure
              console.log(`[OrderDetails] Item ${index}:`, {
                title: item.title,
                product: item.product,
                productType: typeof item.product,
                productIsObject: item.product && typeof item.product === 'object',
                productId: item.product?._id || item.product,
                productImages: item.product?.images,
                productImagesType: typeof item.product?.images,
                productImagesIsArray: Array.isArray(item.product?.images),
                itemImages: item.images
              });
              
              // Get product image - check if product is populated or if images are in item
              let productImage = null;
              
              // Check if product is populated (object with data)
              if (item.product && typeof item.product === 'object' && item.product !== null) {
                // Check if it's a populated product object (has _id or title/name)
                const isPopulatedProduct = item.product._id || item.product.title || item.product.name;
                
                console.log(`[OrderDetails] Item ${index} - isPopulatedProduct:`, isPopulatedProduct);
                
                if (isPopulatedProduct) {
                  // Product is populated - extract image
                  if (item.product.images) {
                    console.log(`[OrderDetails] Item ${index} - product.images exists:`, item.product.images);
                    if (Array.isArray(item.product.images) && item.product.images.length > 0) {
                      // Get first non-empty image
                      productImage = item.product.images.find(img => img && img.trim()) || null;
                      console.log(`[OrderDetails] Item ${index} - Found image from array:`, productImage);
                    } else if (typeof item.product.images === 'string' && item.product.images.trim()) {
                      productImage = item.product.images;
                      console.log(`[OrderDetails] Item ${index} - Found image from string:`, productImage);
                    }
                  } else {
                    console.log(`[OrderDetails] Item ${index} - product.images is falsy`);
                  }
                }
              } else {
                console.log(`[OrderDetails] Item ${index} - Product is not an object or is null`);
              }
              
              // Fallback to item-level images if product image not found
              if (!productImage) {
                console.log(`[OrderDetails] Item ${index} - No product image, checking item-level images`);
                if (item.images) {
                  if (Array.isArray(item.images) && item.images.length > 0) {
                    productImage = item.images.find(img => img && img.trim()) || null;
                  } else if (typeof item.images === 'string' && item.images.trim()) {
                    productImage = item.images;
                  }
                } else if (item.image && typeof item.image === 'string' && item.image.trim()) {
                  productImage = item.image;
                }
              }
              
              console.log(`[OrderDetails] Item ${index} - Final productImage:`, productImage);
              
              // Convert to display URL or use placeholder
              const imageUrl = productImage ? convertGoogleDriveUrlForDisplay(productImage) : getPlaceholderImage(60, 60, 'No Image');
              
              console.log(`[OrderDetails] Item ${index} - Final imageUrl:`, imageUrl.substring(0, 100));
              
              return (
                <div key={index} className="order-item-card">
                  <div className="item-info">
                    <div className="item-name-wrapper">
                      <img 
                        src={imageUrl}
                        alt={item.title || item.name || 'Product'}
                        className="order-item-image"
                        onError={(e) => {
                          // Prevent infinite retry loops
                          if (e.target.dataset.retryCount) {
                            e.target.src = getPlaceholderImage(60, 60, 'No Image');
                            e.target.onerror = null;
                            return;
                          }
                          e.target.dataset.retryCount = '1';
                          
                          const originalSrc = e.target.src;
                          // Skip problematic URLs
                          if (originalSrc.includes('lh3.googleusercontent.com') || 
                              originalSrc.includes('drive.google.com/uc?export=download')) {
                            e.target.src = getPlaceholderImage(60, 60, 'No Image');
                            e.target.onerror = null;
                            return;
                          }
                          
                          e.target.src = getPlaceholderImage(60, 60, 'No Image');
                          e.target.onerror = null;
                        }}
                      />
                      <div className="item-name">{item.title || item.name || 'Product'}</div>
                    </div>
                    {item.sku && (
                      <div className="item-detail">SKU: {item.sku}</div>
                    )}
                    {item.size && (
                      <div className="item-detail">Size: {item.size}</div>
                    )}
                    <div className="item-detail">Quantity: {item.qty || item.quantity}</div>
                  </div>
                  <div className="item-price">₹{(item.price * (item.qty || item.quantity)).toFixed(2)}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Delivery Address */}
        {order.deliveryAddress && (
          <div className="order-section">
            <h3>Delivery Address</h3>
            <div className="address-card">
              <div className="address-name">{order.deliveryAddress.name || order.deliveryAddress.fullName}</div>
              {order.deliveryAddress.phone && (
                <div className="address-phone">📞 {order.deliveryAddress.phone}</div>
              )}
              <div className="address-text">
                {order.deliveryAddress.street || order.deliveryAddress.line1}
                {order.deliveryAddress.floor && `, Floor ${order.deliveryAddress.floor}`}
                {order.deliveryAddress.landmark && `, ${order.deliveryAddress.landmark}`}
                <br />
                {order.deliveryAddress.area && `${order.deliveryAddress.area}, `}
                {order.deliveryAddress.city}, {order.deliveryAddress.state} - {order.deliveryAddress.pincode || order.deliveryAddress.postalCode}
              </div>
            </div>
          </div>
        )}

        {/* Payment & Summary */}
        <div className="order-summary-section">
          <div className="payment-info">
            <h3>Payment Information</h3>
            <div className="payment-details">
              <div className="payment-method">
                <span className="payment-label">Payment Method:</span>
                <span className="payment-value">
                  {order.paymentMethod === 'cod' ? '💵 Cash on Delivery' : order.paymentMethod}
                </span>
              </div>
              {order.paymentMethod === 'cod' && (
                <div className="payment-note">
                  You will pay ₹{order.total?.toFixed(2)} when you receive your order.
                </div>
              )}
            </div>
          </div>

          <div className="order-totals">
            <h3>Order Summary</h3>
            <div className="totals-list">
              <div className="total-row">
                <span>Subtotal</span>
                <span>₹{order.subtotal?.toFixed(2) || '0.00'}</span>
              </div>
              {order.shipping > 0 && (
                <div className="total-row">
                  <span>Shipping</span>
                  <span>₹{order.shipping.toFixed(2)}</span>
                </div>
              )}
              <div className="total-row final-total">
                <span>Total</span>
                <span>₹{order.total?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Order Notes */}
        {order.notes && (
          <div className="order-section">
            <h3>Order Notes</h3>
            <div className="order-notes">
              {order.notes}
            </div>
          </div>
        )}
      </div>

      {showLoginModal && (
        <LoginModal 
          onClose={() => {
            setShowLoginModal(false);
            // If still not authenticated, redirect to home
            if (!isAuthenticated) {
              navigate('/');
            }
          }} 
        />
      )}
    </div>
  );
}

